import { ColumnDef } from "@tanstack/react-table";
import useDataTable from "hooks/useDataTable";
import React, { useCallback, useEffect, useState } from "react";
import { createSelector } from "reselect";
import { useDispatch, useSelector } from "react-redux";
import DataTableTemplate from "Components/templates/data-table-template";
import ActionButton from "Components/atoms/buttons/action-button";
import Chip from "Components/atoms/chip";
import withRouter from "Components/Common/withRouter";
import IconButton from "Components/atoms/buttons/icon-button";
import TableRowControlsContainer from "Components/atoms/containers/table-row-controls-container";
import { PencilIcon, TrashIcon, PlusIcon, HelpIcon } from "Components/atoms/icons";
import useManual from "hooks/useManual";
import AreYouSureModal from "Components/organisms/are-you-sure-modal";
import useAreYouSureModal from "hooks/useAreYouSureModal";
import { ProfileSliceState } from "slices/auth/profile/reducer";
import { ContinuingEducationSliceState, setContinuingEducationRequests, setContinuingEducationRequestCount, removeContinuingEducationRequest } from "slices/forms/continuing-education/reducer";
import { getContinuingEducationRequests, deleteContinuingEducationRequest, downloadContinuingEducationCsv } from "slices/forms/continuing-education/thunk";
import ContinuingEducationRequest, { IContinuingEducationQueryParams } from "models/continuing-education";
import { CONTINUING_EDUCATION_STATUS, CONTINUING_EDUCATION_STATUS_VARIANT } from "models/continuing-education-status";
import TableLink from "Components/atoms/table-link";
import { navigateToContinuingEducationDetails, navigateToCreateContinuingEducation, navigateToEditContinuingEducation } from "utils/navigator";
import { canEdit, isDeclined } from "utils/continuing-education";
import { formatCurrency, formatDateTimeAmPm } from "utils/tools";

// Each approver's own chip status — independent of the request's overall status.
const approverStatus = (decidedAt?: Date | string | null): CONTINUING_EDUCATION_STATUS =>
    decidedAt ? CONTINUING_EDUCATION_STATUS.APPROVED : CONTINUING_EDUCATION_STATUS.PENDING

const selectContinuingEducation = createSelector(
    (state: any) => state.ContinuingEducation,
    ({ requests, requestCount }: ContinuingEducationSliceState) => ({
        requests,
        requestCount
    })
)

const selectProfile = createSelector(
    (state: any) => state.Profile,
    ({ employee }: ProfileSliceState) => ({ employee })
);

const DEFAULT_QUERY: IContinuingEducationQueryParams = {
    page: 0,
    limit: Number.MAX_SAFE_INTEGER
}

const ContinuingEducationListPage = (props: any) => {
    document.title = "Continuing Education | IbexIQ";
    const { handleOpenManual } = useManual("forms/continuing-education");
    const [query] = useState<IContinuingEducationQueryParams>(DEFAULT_QUERY);
    const [isLoading, setIsLoading] = useState<boolean>(true);
    const [isExportingData, setIsExportingData] = useState<boolean>(false);
    const dispatch: any = useDispatch();

    const { requests, requestCount } = useSelector(selectContinuingEducation)
    const { employee } = useSelector(selectProfile)
    const confirmModal = useAreYouSureModal()

    const intializePage = async (q: IContinuingEducationQueryParams) => {
        setIsLoading(true)
        await fetchRequests(q);
        setIsLoading(false)
    }

    const fetchRequests = async (q: IContinuingEducationQueryParams): Promise<boolean> => {
        const result = await getContinuingEducationRequests(q);

        if (!result) {
            return false;
        }

        dispatch(setContinuingEducationRequests(result.data))
        dispatch(setContinuingEducationRequestCount(result.count))
        return true;
    }

    const handleAddRequest = () => navigateToCreateContinuingEducation(props.router.navigate)
    const handleViewRequest = (request: ContinuingEducationRequest) => navigateToContinuingEducationDetails(props.router.navigate, request.id)
    const handleEditRequest = (request: ContinuingEducationRequest) => navigateToEditContinuingEducation(props.router.navigate, request.id)

    const handleDeleteRequest = (request: ContinuingEducationRequest) => {
        confirmModal.open({
            title: "Delete Request",
            subTitle: `Are you sure you want to delete ${request.courseName}?`,
            acceptText: "Delete",
            processingText: "Deleting...",
            action: async () => {
                const success = await deleteContinuingEducationRequest(request.id)
                if (success) dispatch(removeContinuingEducationRequest(request.id))
                return success
            }
        })
    }

    const handleExportToCSV = useCallback(async () => {
        setIsExportingData(true)
        try {
            await downloadContinuingEducationCsv(query)
        } finally {
            setIsExportingData(false)
        }
    }, [query])

    // Load Continuing Education requests
    useEffect(() => {
        intializePage(query)
    }, [])

    const columns = React.useMemo<ColumnDef<ContinuingEducationRequest>[]>(() => [
        {
            header: "Course",
            accessorKey: "courseName",
            size: 160,
            cell: ({ row }) => {
                const canDelete = row.original.employeeId === employee?.id
                    && row.original.status?.description === CONTINUING_EDUCATION_STATUS.PENDING
                return <TableRowControlsContainer>
                    <TableLink onClick={() => handleViewRequest(row.original)}>
                        {row.original.courseName}
                    </TableLink>
                    <div className='d-flex flex-row justify-content-end'>
                        {canEdit(row.original, employee?.id) ? <IconButton title={isDeclined(row.original) ? "Edit & Resubmit" : "Edit"} onClick={() => handleEditRequest(row.original)}><PencilIcon /></IconButton> : undefined}
                        {canDelete ? <IconButton title="Delete" onClick={() => handleDeleteRequest(row.original)}><TrashIcon /></IconButton> : undefined}
                    </div>
                </TableRowControlsContainer>
            },
        },
        {
            header: "Employee",
            id: "employee",
            accessorFn: (row) => row.employee
                ? (row.employee.commonName ?? row.employee.firstName) + " " + row.employee.lastName
                : undefined,
            size: 120,
            cell: ({ row }) => row.original.employee
                ? (row.original.employee.commonName ?? row.original.employee.firstName) + " " + row.original.employee.lastName
                : "Not found",
        },
        {
            header: "Cost (CAD)",
            id: "cost",
            accessorFn: (row) => Number(row.cost),
            cell: ({ getValue }) => formatCurrency(getValue()),
            size: 90,
        },
        {
            header: "Remaining Budget (CAD)",
            id: "remainingBudget",
            accessorFn: (row) => Number(row.remainingBudget),
            cell: ({ getValue }) => formatCurrency(getValue()),
            size: 120,
        },
        {
            header: "Manager",
            id: "manager",
            accessorFn: (row) => row.managerApprover
                ? `${row.managerApprover.commonName ?? row.managerApprover.firstName} ${row.managerApprover.lastName}`
                : undefined,
            size: 140,
            cell: ({ row }) => {
                const isDeclined = row.original.status?.description === CONTINUING_EDUCATION_STATUS.DECLINED
                const status = approverStatus(row.original.managerDecidedAt)
                return <span style={{ display: 'inline-flex', alignItems: 'center', gap: '6px' }}>
                    {row.original.managerApprover?.commonName ?? row.original.managerApprover?.firstName} {row.original.managerApprover?.lastName}
                    {!isDeclined && <Chip variant={CONTINUING_EDUCATION_STATUS_VARIANT[status]}>{status}</Chip>}
                </span>
            }
        },
        {
            header: "Secondary Approver",
            id: "secondaryApprover",
            accessorFn: (row) => row.secondaryApproverId && row.secondaryApprover
                ? `${row.secondaryApprover.commonName ?? row.secondaryApprover.firstName} ${row.secondaryApprover.lastName}`
                : undefined,
            size: 140,
            cell: ({ row }) => {
                if (!row.original.secondaryApproverId) return "Not needed"

                const isDeclined = row.original.status?.description === CONTINUING_EDUCATION_STATUS.DECLINED
                const status = approverStatus(row.original.secondaryDecidedAt)
                return <span style={{ display: 'inline-flex', alignItems: 'center', gap: '6px' }}>
                    {row.original.secondaryApprover?.commonName ?? row.original.secondaryApprover?.firstName} {row.original.secondaryApprover?.lastName}
                    {!isDeclined && <Chip variant={CONTINUING_EDUCATION_STATUS_VARIANT[status]}>{status}</Chip>}
                </span>
            }
        },
        {
            header: "Status",
            id: "status",
            accessorFn: (row) => row.status?.description,
            size: 90,
            cell: ({ row }) => {
                const status = row.original.status?.description
                if (!status) return <Chip>Not found</Chip>
                return <Chip variant={CONTINUING_EDUCATION_STATUS_VARIANT[status]}>{status}</Chip>
            },
        },
        {
            header: "Submitted At",
            id: "submittedAt",
            accessorFn: (row) => row.submittedAt ? new Date(row.submittedAt).getTime() : undefined,
            size: 100,
            cell: ({ row }) => formatDateTimeAmPm(row.original.submittedAt),
        },
    ], [employee]);

    const table = useDataTable({
        columns,
        data: requests,
        columnResizeMode: 'onEnd',
        enableColumnResizing: true
    })

    return <DataTableTemplate
        fullExport={handleExportToCSV}
        stickyFoot={false}
        title={"Forms"}
        breadcrumbItem={"Continuing Education"}
        table={table}
        columns={columns}
        isLoading={isLoading}
        rowCount={requestCount}
        actions={<>
            <ActionButton onClick={handleAddRequest}>
                Add Request <PlusIcon />
            </ActionButton>
            <ActionButton onClick={handleOpenManual}>Help <HelpIcon /></ActionButton>
        </>}
        modal={<AreYouSureModal {...confirmModal.modalProps} />}
    />
}

export default withRouter(ContinuingEducationListPage);
